import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { useWishlistStore } from '../store/wishlistStore';
import { useUiStore } from '../store/uiStore';

export const useAuth = () => {
  const { user, isLoggedIn, setAuth, logout: clearStore } = useAuthStore();
  const clearWishlist = useWishlistStore((s) => s.clear);
  const closeModal    = useUiStore((s) => s.closeAuthModal);
  const qc = useQueryClient();

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authService.login(email, password),
    onSuccess: ({ user: u, accessToken }) => {
      setAuth(u, accessToken);
      closeModal();
      toast.success(`Welcome back, ${u.firstName ?? u.email}!`);
      qc.invalidateQueries({ queryKey: ['wishlist'] });
    },
    onError: () => toast.error('Invalid email or password'),
  });

  const registerMutation = useMutation({
    mutationFn: (p: { email: string; password: string; firstName?: string; lastName?: string }) =>
      authService.register(p.email, p.password, p.firstName, p.lastName),
    onSuccess: ({ user: u, accessToken }) => {
      setAuth(u, accessToken);
      closeModal();
      toast.success('Account created! Welcome aboard 🎉');
    },
    onError: () => toast.error('Registration failed. Please try again.'),
  });

  const logoutMutation = useMutation({
    mutationFn: authService.logout,
    onSettled: () => {
      clearStore();
      clearWishlist();
      qc.clear();
      toast.success('Logged out');
    },
  });

  return {
    user,
    isLoggedIn,
    login:    loginMutation.mutate,
    register: registerMutation.mutate,
    logout:   logoutMutation.mutate,
    loginWithAmazon: authService.loginWithAmazon,
    isLoggingIn:   loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut:  logoutMutation.isPending,
  };
};
